/**
 * Low-level API client functions for SendRFP
 *
 * This file contains pure API functions for data fetching.
 * All functions take an axios instance as a parameter, making them testable.
 * These functions are consumed by React Query hooks in queries.ts.
 *
 * NOTE: All functions require an axios instance.
 * For testing, use createMockApiClient from @platform/api.
 */

import { logger } from "@platform/browser-logger";
import { getCoverageTypeOrder } from "@platform/types";

import { COVERAGE_TYPE_TO_LABEL } from "../constants";

import {
  BrokerUserOption,
  BtxCoverageResponse,
  CalculateCarrierDueDateResponse,
  RfpBrokerageResponse,
  RfpCarriersResponse,
  RfpClientDetailsResponse,
  RfpDetailsResponse,
  RfpWorkbenchSendResponse,
  SendRfpBrokerageDetails,
  SendRfpCarrier,
  SendRfpClientDetails,
  SendRfpCoverage,
  SendRfpSubmissionData,
  UpdateRfpBrokerageRequest,
  UpdateRfpClientRequest,
  UpdateRfpDetailsRequest,
} from "./types";

import type { AxiosInstance } from "axios";
import type { BrokerPartnerUsersResponse } from "@platform/types";

// ============================================================================
// Transform Functions
// ============================================================================

/**
 * Transforms raw API response into simplified carrier objects for UI
 */
export const transformCarriersResponse = (
  response: RfpCarriersResponse,
): SendRfpCarrier[] => {
  const carriers: SendRfpCarrier[] = [];

  // Process connected carriers
  if (response.connected) {
    response.connected.forEach((carrier) => {
      // Skip carriers without a stable identifier
      const id = carrier.carrierId?.toString() ?? carrier.shareIdentifier;
      if (!id) return;

      const salesRepContact = carrier.contacts?.find(
        (c) => c.type === "sales_rep",
      );
      carriers.push({
        id,
        name: carrier.name,
        status: "connected",
        salesRepEmail: salesRepContact?.email,
        shareIdentifier: carrier.shareIdentifier,
        maxEligibleLives: carrier.maxEligibleLives ?? null,
        restrictionMessage: carrier.message ?? null,
      });
    });
  }

  // Process unconnected carriers
  if (response.unconnected) {
    response.unconnected.forEach((carrier) => {
      // Skip carriers without a stable identifier
      const id = carrier.carrierId?.toString() ?? carrier.shareIdentifier;
      if (!id) return;

      const salesRepContact = carrier.contacts?.find(
        (c) => c.type === "sales_rep",
      );

      const supportContact = carrier.contacts?.find(
        (c) => c.type === "support",
      );
      carriers.push({
        id,
        name: carrier.name,
        status: "unconnected",
        salesRepEmail: salesRepContact?.email,
        supportEmail: supportContact?.email,
        shareIdentifier: carrier.shareIdentifier,
        maxEligibleLives: carrier.maxEligibleLives ?? null,
        restrictionMessage: carrier.message ?? null,
      });
    });
  }

  return carriers;
};

/**
 * Transforms BTX coverage response into SendRfpCoverage format
 */
export const transformCoveragesResponse = (
  coverages: BtxCoverageResponse[],
): SendRfpCoverage[] =>
  coverages
    .map((coverage) => ({
      id: coverage.id,
      type: coverage.type,
      label: COVERAGE_TYPE_TO_LABEL[coverage.type] || coverage.type,
      productType: coverage.productType,
    }))
    .sort(
      (a, b) => getCoverageTypeOrder(a.type) - getCoverageTypeOrder(b.type),
    );

// ============================================================================
// API Functions
// ============================================================================

/**
 * Fetches carriers for an RFP
 *
 * Uses BrokerPortal endpoint: GET /brokerportal/v1/rfp/{rfpId}/carriers
 *
 * @param rfpId - The broker portal RFP ID
 * @param axiosInstance - Axios instance for API calls
 * @returns Promise resolving to raw carriers response
 *
 * @example
 * ```ts
 * const response = await getRfpCarriers("123", axiosInstance);
 * const carriers = transformCarriersResponse(response);
 * ```
 */
export const getRfpCarriers = async (
  rfpId: string,
  axiosInstance: AxiosInstance,
): Promise<RfpCarriersResponse> => {
  logger.debug("[SendRFP API] Fetching RFP carriers:", { rfpId });

  const response = await axiosInstance.get<RfpCarriersResponse>(
    `brokerportal/v1/rfp/${rfpId}/carriers`,
  );

  logger.debug("[SendRFP API] Fetched RFP carriers:", {
    rfpId,
    connected: response.data.connected?.length ?? 0,
    unconnected: response.data.unconnected?.length ?? 0,
  });

  return response.data;
};

/**
 * Fetches coverages for an RFP from BTX service
 *
 * Uses BTX endpoint: GET /btx/v1/rfps/{rfpId}/coverages
 *
 * @param ecoRfpId - The ECO RFP ID (BTX internal ID)
 * @param axiosInstance - Axios instance for API calls
 * @returns Promise resolving to array of coverages
 *
 * @example
 * ```ts
 * const response = await getRfpCoverages("abc-123", axiosInstance);
 * const coverages = transformCoveragesResponse(response);
 * ```
 */
export const getRfpCoverages = async (
  ecoRfpId: string,
  axiosInstance: AxiosInstance,
): Promise<BtxCoverageResponse[]> => {
  logger.debug("[SendRFP API] Fetching RFP coverages:", { ecoRfpId });

  const response = await axiosInstance.get<BtxCoverageResponse[]>(
    `btx/v1/rfps/${ecoRfpId}/coverages`,
  );

  logger.debug("[SendRFP API] Fetched RFP coverages:", {
    ecoRfpId,
    count: response.data.length,
  });

  return response.data;
};

// ============================================================================
// Client Details API
// ============================================================================

/**
 * Fetches client details for an RFP from BrokerPortal
 *
 * Uses BrokerPortal endpoint: GET /brokerportal/v1/rfp/{rfpId}/client
 *
 * @param rfpId - The broker portal RFP ID
 * @param axiosInstance - Axios instance for API calls
 * @returns Promise resolving to client details response
 *
 * @example
 * ```ts
 * const response = await getRfpClientDetails("123", axiosInstance);
 * const clientDetails = transformClientDetailsResponse(response);
 * ```
 */
export const getRfpClientDetails = async (
  rfpId: string,
  axiosInstance: AxiosInstance,
): Promise<RfpClientDetailsResponse> => {
  logger.debug("[SendRFP API] Fetching RFP client details:", { rfpId });

  const response = await axiosInstance.get<RfpClientDetailsResponse>(
    `brokerportal/v1/rfp/${rfpId}/client`,
  );

  logger.debug("[SendRFP API] Fetched RFP client details:", {
    rfpId,
    clientName: response.data.name,
  });

  return response.data;
};

/**
 * Transforms raw client details API response to simplified UI format
 *
 * Extracts only the fields needed for the SendRFP UI.
 *
 * @param response - Raw API response from getRfpClientDetails
 * @returns Simplified client details for UI
 */
export const transformClientDetailsResponse = (
  response: RfpClientDetailsResponse,
): SendRfpClientDetails => ({
  id: response.id,
  name: response.name,
  address: response.address
    ? (() => {
        const addr = response.address as unknown as Record<string, unknown>;
        return {
          addressLine1: (addr["addressLine1"] as string) ?? null,
          addressLine2: (addr["addressLine2"] as string) ?? null,
          city: (addr["city"] as string) ?? null,
          state: (addr["state"] as string) ?? null,
          zipCode: (addr["zipCode"] as string) ?? null,
        };
      })()
    : null,
  situsState: response.situsState,
  situsStateName: response.situsStateName,
  numberOfEmployees: response.numberOfEmployees,
  hasInternationalEmployees: response.hasInternationalEmployees,
  benAdminSystem: response.rfpAdditionalInfo?.requestedSystem ?? null,
  technologyCreditNeeded:
    response.rfpAdditionalInfo?.technologyCreditNeeded ?? null,
  techCreditType: response.rfpAdditionalInfo?.techCreditType ?? null,
  techCreditAmount: response.rfpAdditionalInfo?.techCreditAmount ?? null,
});

// ============================================================================
// Brokerage API
// ============================================================================

/**
 * Helper to combine first and last name into full name
 */
const formatFullName = (
  firstName: string | null,
  lastName: string | null,
): string | null => {
  const parts = [firstName, lastName].filter(Boolean);
  return parts.length > 0 ? parts.join(" ") : null;
};

/**
 * Transforms raw brokerage API response to simplified UI format
 *
 * Extracts broker name, address, submitter, and producer for the SendRFP UI.
 *
 * @param response - Raw API response from getRfpBrokerage
 * @returns Simplified brokerage details for UI
 */
export const transformBrokerageResponse = (
  response: RfpBrokerageResponse,
): SendRfpBrokerageDetails => ({
  id: response.id,
  name: response.name,
  address: response.address,
  submitter: {
    name: formatFullName(
      response.submitter?.firstName,
      response.submitter?.lastName,
    ),
    email: response.submitter?.email ?? null,
  },
  producer: {
    firstName: response.clientInfo?.producer?.firstName ?? null,
    lastName: response.clientInfo?.producer?.lastName ?? null,
    email: response.clientInfo?.producer?.email ?? null,
  },
});

export const getBrokerPartnerUsers = async (
  brokerId: number,
  axiosInstance: AxiosInstance,
): Promise<BrokerPartnerUsersResponse> => {
  const response = await axiosInstance.post<BrokerPartnerUsersResponse>(
    "brokerportal/v1/brokerPartnerUsers",
    { brokerId },
  );

  return response.data;
};

export const transformBrokerPartnerUsersResponse = (
  response: BrokerPartnerUsersResponse,
): BrokerUserOption[] => {
  const seen = new Set<string>();

  return [...response.members, ...response.owners]
    .filter((user) => user.accountEnabled)
    .filter((user) => {
      if (seen.has(user.id)) {
        return false;
      }

      seen.add(user.id);
      return true;
    })
    .map((user) => ({
      id: user.id,
      name: [user.givenName, user.surname].filter(Boolean).join(" ").trim(),
      email: user.mail,
    }))
    .sort((left, right) => left.name.localeCompare(right.name));
};

// ============================================================================
// RFP Submission API
// ============================================================================

/**
 * Fetches RFP submission data for the Send flow
 *
 * Uses BrokerPortal endpoint: GET /brokerportal/v1/rfp/{rfpId}/workbench/send
 *
 * @param rfpId - The broker portal RFP ID
 * @param axiosInstance - Axios instance for API calls
 * @returns Promise resolving to workbench send response
 */
export const getRfpWorkbenchSend = async (
  rfpId: string,
  axiosInstance: AxiosInstance,
): Promise<RfpWorkbenchSendResponse> => {
  logger.debug("[SendRFP API] Fetching RFP workbench send data:", { rfpId });

  const response = await axiosInstance.get<RfpWorkbenchSendResponse>(
    `brokerportal/v1/rfp/${rfpId}/workbench/send`,
  );

  logger.debug("[SendRFP API] Fetched RFP workbench send data:", {
    rfpId,
    effectiveDate: response.data.effectiveDate,
    carrierDueDate: response.data.carrierDueDate,
  });

  return response.data;
};

/**
 * Auto-populates the carrier due date when it is empty and returns it
 *
 * Uses BrokerPortal endpoint: POST /brokerportal/v1/rfp/{rfpId}/carrier-due-date
 */
export const calculateCarrierDueDate = async (
  rfpId: string,
  axiosInstance: AxiosInstance,
): Promise<CalculateCarrierDueDateResponse> => {
  const response = await axiosInstance.post<CalculateCarrierDueDateResponse>(
    `brokerportal/v1/rfp/${rfpId}/carrier-due-date`,
  );

  return response.data;
};

/**
 * Transforms workbench/send response to simplified format
 */
export const transformWorkbenchSendResponse = (
  response: RfpWorkbenchSendResponse,
): SendRfpSubmissionData => ({
  ecoRfpId: response.ecoRfpId,
  brokerName: response.brokerName ?? null,
  clientName: response.clientName,
  effectiveDate: response.revisedEffectiveDate || response.effectiveDate,
  carrierDueDate: response.carrierDueDate ?? null,
  numberOfEmployees: response.numberOfEmployees,
  primaryAnalystEmail: response.primaryAnalyst?.email ?? null,
});

// ============================================================================
// Update Functions
// ============================================================================

/**
 * Updates RFP client details
 * Uses BrokerPortal endpoint: PATCH /brokerportal/v1/rfp/{rfpId}/client
 */
export const updateRfpClient = async (
  rfpId: string,
  data: UpdateRfpClientRequest,
  axiosInstance: AxiosInstance,
): Promise<void> => {
  logger.debug("[SendRFP API] Updating RFP client details:", { rfpId, data });

  await axiosInstance.patch(`brokerportal/v1/rfp/${rfpId}/client`, data);

  logger.debug("[SendRFP API] Successfully updated RFP client details:", {
    rfpId,
  });
};

/**
 * Updates RFP brokerage details
 * Uses BrokerPortal endpoint: PATCH /brokerportal/v1/rfp/{rfpId}/brokerage
 */
export const updateRfpBrokerage = async (
  rfpId: string,
  data: UpdateRfpBrokerageRequest,
  axiosInstance: AxiosInstance,
): Promise<void> => {
  logger.debug("[SendRFP API] Updating RFP brokerage details:", {
    rfpId,
    data,
  });

  await axiosInstance.patch(`brokerportal/v1/rfp/${rfpId}/brokerage`, data);

  logger.debug("[SendRFP API] Successfully updated RFP brokerage details:", {
    rfpId,
  });
};

/**
 * Updates RFP review details (carrier due date, analysts, etc.)
 * Uses BrokerPortal endpoint: PATCH /brokerportal/v1/rfp/{rfpId}/details
 */
export const updateRfpDetails = async (
  rfpId: string,
  data: UpdateRfpDetailsRequest,
  axiosInstance: AxiosInstance,
): Promise<void> => {
  logger.debug("[SendRFP API] Updating RFP details:", { rfpId, data });

  await axiosInstance.patch(`brokerportal/v1/rfp/${rfpId}/details`, data);

  logger.debug("[SendRFP API] Successfully updated RFP details:", { rfpId });
};

/**
 * Fetches full RFP details for an RFP
 * Uses BrokerPortal endpoint: GET /brokerportal/v1/rfp/{rfpId}/details
 */
export const getRfpDetails = async (
  rfpId: string,
  axiosInstance: AxiosInstance,
): Promise<RfpDetailsResponse> => {
  logger.debug("[SendRFP API] Fetching RFP details:", { rfpId });

  const response = await axiosInstance.get<RfpDetailsResponse>(
    `brokerportal/v1/rfp/${rfpId}/details`,
  );

  logger.debug("[SendRFP API] RFP details fetched:", {
    count: response.data.analystsList?.length ?? 0,
  });

  return response.data;
};
